// ETF Wealth Builder Calculator -- the Recharts "Fan Chart" (probability cone).
//
// The visual trick: to draw a SHADED band floating between the 10th and 90th
// percentile, we feed <Area> a 2-element array `[p10, p90]` per data point.
// When Recharts receives an array for a dataKey it fills only the space BETWEEN
// the two values -- perfectly rendering the "Cone of Uncertainty".

import React, { useMemo } from 'react';
import {
    Area,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type { YearlySimulationData } from '../types';

interface FanChartProps {
    data: YearlySimulationData[];
    isUcits: boolean;
}

export const FanChart: React.FC<FanChartProps> = ({ data, isUcits }) => {
    const currencySymbol = isUcits ? '€' : '$';

    // Compact Y-axis labels (e.g. €1.2M / $500k) keep the chart uncluttered.
    const formatYAxis = (val: number): string => {
        if (val >= 1_000_000) return `${currencySymbol}${(val / 1_000_000).toFixed(1)}M`;
        if (val >= 1_000) return `${currencySymbol}${(val / 1_000).toFixed(0)}k`;
        return `${currencySymbol}${val}`;
    };

    // Signature widened to Recharts' ValueType / NameType so the prop typechecks
    // under `tsc -b` (strict) without a cast.
    const formatTooltip = (
        value: number | string | Array<number | string>,
        name: string | number,
    ): [React.ReactNode, React.ReactNode] => {
        // Hide the raw [p10, p90] band array from the hover tooltip.
        if (Array.isArray(value)) return [null, null];
        const num = typeof value === 'number' ? value : Number(value);
        return [
            new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: isUcits ? 'EUR' : 'USD',
                maximumFractionDigits: 0,
            }).format(num),
            name,
        ];
    };

    // Recharts-specific transform: pack [p10, p90] into a single array key so
    // <Area> renders it as a floating shaded band.
    const chartData = useMemo(
        () =>
            data.map((d) => ({
                ...d,
                probabilityRange: [d.percentile_10, d.percentile_90],
            })),
        [data],
    );

    return (
        <div className="w-full h-[480px] bg-white p-5 pb-10 rounded-2xl shadow-sm border border-slate-100 mt-6">
            <h3 className="text-lg font-bold text-slate-800 mb-4 text-center">
                Probability Cone (80% Confidence Interval)
            </h3>
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                    data={chartData}
                    margin={{ top: 10, right: 20, left: 10, bottom: 24 }}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis
                        dataKey="year"
                        tickFormatter={(y) => `Yr ${y}`}
                        stroke="#64748b"
                        tick={{ fontSize: 12 }}
                        minTickGap={20}
                    />
                    <YAxis
                        tickFormatter={formatYAxis}
                        stroke="#64748b"
                        tick={{ fontSize: 12 }}
                        width={70}
                    />
                    <Tooltip
                        formatter={formatTooltip}
                        labelFormatter={(label) => `Year ${label}`}
                        contentStyle={{
                            borderRadius: '8px',
                            border: 'none',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        }}
                    />
                    <Legend wrapperStyle={{ paddingTop: '10px' }} />

                    <Area
                        type="monotone"
                        dataKey="probabilityRange"
                        fill="#bae6fd"
                        fillOpacity={0.6}
                        stroke="none"
                        name="80% Probability Band"
                        activeDot={false}
                    />
                    <Line
                        type="monotone"
                        dataKey="percentile_90"
                        stroke="#38bdf8"
                        strokeWidth={2}
                        strokeDasharray="4 4"
                        dot={false}
                        name="Optimistic (90th)"
                    />
                    <Line
                        type="monotone"
                        dataKey="percentile_50"
                        stroke="#0284c7"
                        strokeWidth={3}
                        dot={false}
                        name="Expected (Median)"
                    />
                    <Line
                        type="monotone"
                        dataKey="percentile_10"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        dot={false}
                        name="Pessimistic (10th)"
                    />
                    <Line
                        type="monotone"
                        dataKey="total_contributed"
                        stroke="#475569"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={false}
                        name="Cash Deposited"
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
};
